'use client';
import React from 'react';
import styles from './TopBar.module.scss';
import { AccountMenu } from './AccountMenu';
import { GlobalMenu } from './GlobalMenu';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import i18n from '@benzinga/translate';
import { useTranslation } from 'react-i18next';

import { featuredMenu as TopBarMenu } from './Header/_mockMenu';
import { useHydrate } from '@benzinga/hooks';
import { useRouter, usePathname } from 'next/navigation';
import { getRegisterType } from '@benzinga/utils';
import { ButtonBzPro } from './BenzingaProButton';
import { handleMenuItemsClickEvent } from '../utils';
export { featuredMenu as TopBarMenu } from './Header/_mockMenu';

interface MenuItem {
  href: string;
  logged_in_href?: string;
  title: string;
}

interface TopBarProps {
  items?: MenuItem[];
}

export const TopBar = React.memo(({ items }: TopBarProps) => {
  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);
  const { t } = useTranslation('common', { i18n });
  const itemsList = () => {
    const topBarItems = items ? items : TopBarMenu;

    return topBarItems?.map(item => (
      <li className="bz-top-bar-list-item" key={item.title}>
        <a href={item.href} onClick={() => handleMenuItemsClickEvent('menu_click', 'top_bar', item.title)}>
          <div className="bz-top-bar-list-item-label">{t(`${item.title}`)}</div>
        </a>
      </li>
    ));
  };

  const router = useRouter();
  const routerAsPath = usePathname();

  const requestAuth = React.useCallback(
    action => {
      const redirectUrl = new URL(`${window.location.origin}/login?action=${action}&next=${window.location.href}`);

      const utm_source = getRegisterType(routerAsPath ?? '');
      if (utm_source) {
        redirectUrl.searchParams.append('utm_source', utm_source);
      }

      window.location.href = redirectUrl.toString();
    },
    [routerAsPath],
  );

  const login = React.useCallback(() => {
    requestAuth('login');
  }, [requestAuth]);

  const register = React.useCallback(() => {
    requestAuth('register');
  }, [requestAuth]);

  const logout = React.useCallback(() => {
    let next = window.location.href;
    if (next.includes('/account')) {
      next = window.location.origin;
    }
    next = encodeURIComponent(next);

    const url = new URL(`${window.location.origin}/logout?next=${next}`);
    router.push(url.toString());
  }, [router]);

  return (
    <div className={`${styles.topBarWrapper} top-bar-wrapper`}>
      <div className="bar-layout-width">
        <div className="top-bar-inner-wrapper">
          <ul className="top-bar-list">
            <li className="bz-top-bar-list-item ProLabel">
              <ButtonBzPro
                label="Get Benzinga Pro"
                url="https://www.benzinga.com/pro/register?adType=homepagebluebutton&utm_source=homepage-blue-button&utm_campaign=14daytrial"
                variant="light"
              />
            </li>
            {itemsList()}
          </ul>
          <div className="auth-buttons">
            <div className="contributor-link">
              <a href="https://contributor.benzinga.com/contributor-onboarding">{t('Navigation.TopBar.contributor')}</a>
            </div>
            <GlobalMenu />
            {isLoggedIn ? (
              <React.Suspense>
                <AccountMenu deviceType="desktop" isLoggedIn={isLoggedIn} onLogout={logout} />
              </React.Suspense>
            ) : (
              <>
                <button className="auth-link-button" onClick={login}>
                  {t('Navigation.TopBar.login')}
                </button>
                <button className="auth-link-button bg-orange-600" onClick={register}>
                  {t('Navigation.TopBar.register')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export const TopBarWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={`${styles.topBarWrapper} ${className ?? ''}`} {...props} />
);
